import { useEffect, useState } from "react";
import { Building2, Upload, HandHelping, Loader2, X, CheckCircle2 } from "lucide-react";
import BankerUploadArtifactModal from "../../components/banker-upload-artifact-modal";
import { WheelhouseBadge } from "../../components/wheelhouse-badge";

const BASE = import.meta.env.BASE_URL;

export default function BankerCompanies() {
  const [companies, setCompanies] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadFor, setUploadFor] = useState<number | null>(null);
  const [reqFor, setReqFor] = useState<{ id: number; name: string; kind: "request_to_cover" | "hand_off" } | null>(null);

  const load = async () => {
    const token = localStorage.getItem("banker_token");
    if (!token) return;
    try {
      const r = await fetch(`${BASE}api/banker/companies`, { headers: { "x-banker-token": token } });
      if (r.ok) setCompanies(await r.json());
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const hasCoverage = companies.some(c => c.covered);

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2"><Building2 className="w-7 h-7 text-[#FF5C00]" /> Portfolio companies</h1>
          <p className="text-white/50 text-sm mt-1">Every venture on the platform. You can only act on portcos in your wheelhouse — request a hand-off if you can't carry one, or request to cover one that's not yours.</p>
        </div>
        {hasCoverage && (
          <button onClick={() => setUploadFor(0)} className="bg-[#FF5C00] hover:bg-[#E55200] text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2">
            <Upload className="w-4 h-4" /> Upload artifact
          </button>
        )}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{[...Array(4)].map((_, i) => <div key={i} className="h-32 bg-white/[0.04] rounded-xl animate-pulse" />)}</div>
      ) : companies.length === 0 ? (
        <div className="bg-white/[0.04] border border-white/[0.1] rounded-xl p-12 text-center">
          <p className="text-white/40 text-sm">No companies on the platform yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {companies.map((c) => (
            <div key={c.id} className={`rounded-xl p-5 transition-colors border ${c.covered ? "bg-emerald-500/[0.04] border-emerald-500/25 hover:border-emerald-500/40" : "bg-white/[0.03] border-white/[0.08] hover:border-white/[0.15] opacity-75"}`}>
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 border ${c.covered ? "bg-[#FF5C00]/15 border-[#FF5C00]/25" : "bg-white/5 border-white/10"}`}>
                  <Building2 className={`w-5 h-5 ${c.covered ? "text-[#FF5C00]" : "text-white/40"}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-white">{c.name}</p>
                    <WheelhouseBadge status={c.covered ? "in_wheelhouse" : "other_portco"} />
                  </div>
                  {c.tagline && <p className="text-white/60 text-xs mt-1 line-clamp-2">{c.tagline}</p>}
                  <div className="flex items-center gap-2 mt-2 text-[10px] text-white/40 font-mono">
                    {c.stage && <span>{c.stage}</span>}
                    {c.sector && <span>· {c.sector}</span>}
                  </div>
                  <div className="mt-3 flex items-center gap-3 flex-wrap">
                    {c.covered ? (
                      <>
                        <button onClick={() => setUploadFor(c.id)} className="text-[11px] text-[#FF5C00] hover:text-[#FF8040] font-medium inline-flex items-center gap-1">
                          <Upload className="w-3 h-3" /> Upload artifact
                        </button>
                        <button onClick={() => setReqFor({ id: c.id, name: c.name, kind: "hand_off" })} className="text-[11px] text-amber-400 hover:text-amber-300 font-medium inline-flex items-center gap-1">
                          <HandHelping className="w-3 h-3" /> Hand off
                        </button>
                      </>
                    ) : (
                      <button onClick={() => setReqFor({ id: c.id, name: c.name, kind: "request_to_cover" })} className="text-[11px] text-amber-400 hover:text-amber-300 font-medium inline-flex items-center gap-1">
                        <HandHelping className="w-3 h-3" /> Request to cover
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {uploadFor !== null && (
        <BankerUploadArtifactModal
          defaultCompanyId={uploadFor || null}
          onClose={() => setUploadFor(null)}
          onUploaded={load}
        />
      )}

      {reqFor && <ReallocationModal companyId={reqFor.id} companyName={reqFor.name} kind={reqFor.kind} onClose={() => setReqFor(null)} />}
    </div>
  );
}

function ReallocationModal({ companyId, companyName, kind, onClose }: { companyId: number; companyName: string; kind: "request_to_cover" | "hand_off"; onClose: () => void }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  const submit = async () => {
    setSaving(true); setErr(null);
    const token = localStorage.getItem("banker_token") || "";
    try {
      const r = await fetch(`${BASE}api/banker/me/reallocation-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-banker-token": token },
        body: JSON.stringify({ companyId, kind, reason: reason || null }),
      });
      if (r.ok) { setOk(true); setTimeout(onClose, 1200); }
      else { const e = await r.json(); setErr(e.error || "Could not submit"); }
    } catch { setErr("Network error"); }
    setSaving(false);
  };

  const isHandoff = kind === "hand_off";
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#111] border border-white/[0.1] rounded-2xl p-6 max-w-md w-full">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <HandHelping className="w-5 h-5 text-amber-400" /> {isHandoff ? "Hand off this portco" : "Request to cover"}
          </h2>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-white/60 text-xs leading-relaxed mb-4">
          {isHandoff
            ? <>You'll stop covering <span className="text-white">{companyName}</span> once admin approves. They will assign another banker.</>
            : <>Admin will see this and decide whether to add you as a coverer of <span className="text-white">{companyName}</span>. The current coverer will be notified.</>}
        </p>
        {ok ? (
          <div className="bg-emerald-500/10 border border-emerald-500/25 rounded p-3 text-emerald-300 text-sm flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> Sent.
          </div>
        ) : (
          <>
            {err && <div className="bg-red-500/10 border border-red-500/25 rounded p-2 text-red-400 text-xs mb-3">{err}</div>}
            <label className="block text-white/60 text-xs mb-1.5">Reason {isHandoff ? "(required)" : "(optional)"}</label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={4}
              placeholder={isHandoff ? "e.g. Conflict of interest, or my plate is full and this portco needs faster cycles." : "e.g. I have prior context or capacity to take this on."}
              className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 resize-none" />
            <div className="flex gap-2 mt-4">
              <button onClick={submit} disabled={saving || (isHandoff && reason.trim().length < 5)}
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-black py-2.5 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50">
                {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : "Send request"}
              </button>
              <button onClick={onClose} className="px-4 py-2.5 border border-white/15 rounded-lg text-sm text-white/70">Cancel</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
